<template>
  <div class="loan-search-form">
    <b-form class="form loan-search-form" @submit.stop.prevent="submit">
      <!-- title -->
      <div class="mb-3">
        <h4 class="loan-search-form__title">
          {{ $t("components.loan.search_form.title") }}
        </h4>
      </div>

      <!-- buttons to select types of vehicles -->
      <b-form-group>
        <b-form-checkbox-group
          v-if="possibleLoanableTypes.length > 1"
          id="loanable_type"
          buttons
          button-variant="outline-primary"
          :disabled="loading"
          class="loan-search-form__loanable-buttons"
          :checked="selectedLoanableTypes"
          @change="emitLoanableTypes"
        >
          <b-checkbox
            v-if="possibleLoanableTypes.includes('car')"
            class="no-inset loan-search-form__loanable-buttons__button"
            value="car"
          >
            <svg-car />
            <div class="loan-search-form__loanable-buttons__button__label">
              {{ $t("loanables.fields.types.car") }}
            </div>
          </b-checkbox>

          <b-checkbox
            v-if="possibleLoanableTypes.includes('bike')"
            class="no-inset loan-search-form__loanable-buttons__button"
            value="bike"
          >
            <svg-bike />
            <div class="loan-search-form__loanable-buttons__button__label">
              {{ $t("loanables.fields.types.bike") }}
            </div>
          </b-checkbox>

          <b-checkbox
            v-if="possibleLoanableTypes.includes('trailer')"
            class="no-inset loan-search-form__loanable-buttons__button"
            value="trailer"
          >
            <svg-trailer />
            <div class="loan-search-form__loanable-buttons__button__label">
              {{ $t("loanables.fields.types.trailer") }}
            </div>
          </b-checkbox>

          <b-checkbox
            v-if="possibleLoanableTypes.includes('car_trailer')"
            class="no-inset loan-search-form__loanable-buttons__button"
            value="car_trailer"
          >
            <svg-car-trailer />
            <div class="loan-search-form__loanable-buttons__button__label">
              {{ $t("loanables.fields.types.car_trailer") }}
            </div>
          </b-checkbox>
        </b-form-checkbox-group>
      </b-form-group>

      <borrower-profile-warning
        v-if="hasSelectedRestrictedLoanableTypes && !canBorrowCar"
        :community-restricted-loanable-types="communityRestrictedLoanableTypes.join('+')"
      />

      <div class="loan-search-form__search-dates">
        <div class="search-date-input">
          <label>{{ $t("loans.fields.departure_at") | capitalize }}</label>
          <forms-date-time-picker
            ref="depature"
            zoneless
            :value="searchDate"
            :disabled-dates-fct="disabledBeforeToday"
            :disabled-times-fct="disabledBeforeNow"
            @input="searchDateChanged"
          >
            <template #footer>
              <button class="mx-btn mx-btn-text" @click="setToEarliestDeparture">
                {{ $t("components.loan.new_loan_form.duration.now") }}
              </button>
            </template>
          </forms-date-time-picker>
        </div>

        <div class="search-date-input">
          <label>{{ $t("loans.fields.return_at") | capitalize }}</label>

          <forms-date-time-picker
            ref="return"
            :value="returnAt"
            zoneless
            :disabled-dates-fct="disabledBeforeDepartureDay"
            :disabled-times-fct="disabledBeforeDeparture"
            @input="returnAtChanged"
          >
            <template #footer>
              <button class="mx-btn mx-btn-text" @click="() => storeDurationAndClosePopup(60)">
                {{ $t("components.loan.new_loan_form.duration.one_hour") }}
              </button>
              <button class="mx-btn mx-btn-text" @click="() => storeDurationAndClosePopup(60 * 3)">
                {{ $t("components.loan.new_loan_form.duration.three_hours") }}
              </button>
              <button class="mx-btn mx-btn-text" @click="() => storeDurationAndClosePopup(60 * 24)">
                {{ $t("components.loan.new_loan_form.duration.one_day") }}
              </button>
            </template>
          </forms-date-time-picker>
        </div>
      </div>

      <!-- text for loan invalid duration -->
      <b-alert v-if="invalidDuration" show variant="danger">
        {{ $t("components.loan.new_loan_form.duration.invalid") }}
      </b-alert>

      <div class="estimated-distance">
        <forms-validated-input
          :label="capitalize($t('loans.fields.estimated_distance'))"
          name="estimated_distance"
          type="number"
          :value="estimatedDistance"
          @input="$emit('update:estimated-distance', $event)"
        />
      </div>

      <!-- search button -->
      <div class="button-list justify-content-between">
        <icon-button
          type="submit"
          pill
          variant="success"
          :disabled="loading || invalidDuration"
          :loading="loading"
          icon="search"
        >
          {{ $t("forms.search") | capitalize }}
        </icon-button>
        <slot name="buttons"></slot>
      </div>
      <!---->
    </b-form>
  </div>
</template>

<script>
import dayjs from "dayjs";

import Bike from "@/assets/svg/bike.svg";
import Car from "@/assets/svg/car.svg";
import CarTrailer from "@/assets/svg/car_trailer.svg";
import Trailer from "@/assets/svg/trailer.svg";

import FormsDateTimePicker from "@/components/Forms/DateTimePicker.vue";
import FormsValidatedInput from "@/components/Forms/ValidatedInput";
import BorrowerProfileWarning from "@/components/Loan/BorrowerProfileWarning.vue";
import IconButton from "@/components/shared/IconButton.vue";

import { capitalize } from "@/helpers/filters";
import { getEarliestDeparture } from "@/helpers/loanDepartures";
import { canBorrowCar } from "@/helpers/permissions/users";

import FormLabelsMixin from "@/mixins/FormLabelsMixin";

export default {
  name: "SearchForm",
  components: {
    FormsDateTimePicker,
    FormsValidatedInput,
    IconButton,
    BorrowerProfileWarning,
    "svg-bike": Bike,
    "svg-car": Car,
    "svg-car-trailer": CarTrailer,
    "svg-trailer": Trailer,
  },
  mixins: [FormLabelsMixin],
  props: {
    estimatedDistance: {
      required: false,
      type: [Number, String],
      default: null,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    selectedLoanableTypes: {
      type: Array,
      required: true,
    },
    possibleLoanableTypes: {
      type: Array,
      required: true,
    },
  },
  computed: {
    canBorrowCar() {
      return canBorrowCar(this.user);
    },
    communityRestrictedLoanableTypes() {
      return this.possibleLoanableTypes.filter((p) => ["car", "car_trailer"].includes(p));
    },
    hasSelectedRestrictedLoanableTypes() {
      return (
        this.selectedLoanableTypes.filter((p) => this.communityRestrictedLoanableTypes.includes(p))
          .length > 0
      );
    },
    invalidDuration() {
      // Invalid if the duration of a loan is not greater than 0 minute.
      return !(this.searchDuration > 0);
    },
    searchDate() {
      return this.$store.state["loanable.search"].searchDepartureAt;
    },
    searchDuration() {
      return this.$store.state["loanable.search"].searchDuration;
    },
    returnAt() {
      return dayjs
        .utc(this.searchDate)
        .add(this.searchDuration, "minute")
        .format("YYYY-MM-DD HH:mm");
    },
    user() {
      return this.$store.state.user;
    },
  },
  beforeMount() {
    const { searchDepartureAt, searchDuration } = this.$store.state["loanable.search"];
    const minimumTime = getEarliestDeparture();
    if (!searchDepartureAt || minimumTime.isAfter(searchDepartureAt)) {
      this.$store.commit("loanable.search/searchDepartureAt", minimumTime);
    }
    if (!searchDuration) {
      this.$store.commit("loanable.search/searchDuration", 60);
    }
  },
  methods: {
    capitalize,
    disabledBeforeDeparture(date) {
      return dayjs(date).isSameOrBefore(this.searchDate, "minute");
    },
    disabledBeforeDepartureDay(date) {
      return dayjs(date).isBefore(this.searchDate, "day");
    },
    disabledBeforeNow(date) {
      return dayjs().isSameOrAfter(date, "minute") || dayjs(date).minute() > 45;
    },
    disabledBeforeToday(date) {
      return dayjs().isAfter(date, "day");
    },
    emitLoanableTypes(value) {
      this.$emit("selectLoanableTypes", value);
    },
    returnAtChanged(returnAt) {
      this.storeSearchDuration(dayjs.utc(returnAt).diff(dayjs.utc(this.searchDate), "minute"));
    },
    searchDateChanged(newDepartureAt) {
      if (this.invalidDuration) {
        // If duration is negative, we allow user to move each date independently
        const newDuration = dayjs.utc(this.returnAt).diff(dayjs.utc(newDepartureAt), "minute");
        this.storeSearchDuration(newDuration);
      }
      this.storeSearchDate(newDepartureAt);
    },
    storeDurationAndClosePopup(duration) {
      this.storeSearchDuration(duration);
      this.$refs.return.close();
    },
    storeSearchDuration(duration) {
      this.$store.commit("loanable.search/searchDuration", duration);
      this.$emit("update:search-duration", duration);
    },
    storeSearchDate(date) {
      this.$store.commit("loanable.search/searchDepartureAt", date);
    },
    setToEarliestDeparture() {
      this.storeSearchDate(getEarliestDeparture());
      this.$refs.depature.close();
    },
    submit() {
      this.$emit("submit");
    },
  },
};
</script>

<style lang="scss">
@import "~bootstrap/scss/mixins/breakpoints";

.loan-search-form {
  container-type: inline-size;

  &__search-dates {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.5rem;
    @container (width > 30rem) {
      flex-direction: row;
    }
  }

  &__title {
    font-size: 22px;
    font-weight: 700;
    margin: 0;
  }

  .b-icon.bi {
    font-size: 100%;
    vertical-align: -0.15em;
  }

  &__loanable-buttons {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;

    &__button__label {
      line-height: 12px;
      min-height: 24px;
    }

    svg {
      height: 22px;
      width: 34px;

      path {
        fill: currentColor;
      }
    }

    label {
      &.btn {
        border-radius: 10px;
        font-size: 13px;
        line-height: 24px;
        display: flex;
        flex-direction: column;
        flex-basis: 5rem;
        border-width: 2px;
        border-radius: 10px !important;
        padding-left: 6px;
        padding-right: 6px;
        align-items: center;
        justify-content: flex-end;
      }

      &:hover {
        background-color: #fff;
        color: $primary;
      }

      &.disabled {
        background-color: #fff;
        border-color: $light !important;
        color: #7a7a7a;

        &.active {
          background-color: $light;
        }
      }
    }
  }
}
</style>
